<template>
  <div class="cn-product-images-slider">
    <div class="cn-product-images-slider__content">
      <div class="cn-product-images-slider__preview">
        <!-- swiper1 -->
        <swiper-container
          v-show="isSliderImagesInit"
          ref="swiperEl"
          init="false"
          class="swiper gallery-top"
        >
          <swiper-slide
            v-for="(image, i) in images"
            :key="i"
            class="cursor-pointer"
            @click="gallery?.open(i)"
          >
            <CdnImg
              :src="image.src || '/img/placeholder.png'"
              :alt="image.alt"
              loading="lazy"
              :width="1024"
              :height="1024"
              itemprop="image"
              format="webp"
            />
          </swiper-slide>
        </swiper-container>
      </div>
      <div class="cn-product-images-slider__preview-controls">
        <button
          class="cn-product-images-slider__preview-prev"
          @click="slidePrev"
        >
          <UiIcon name="chevron-left" />
        </button>
        <div class="swiper-pagination">
          {{ activeThumbIndex + 1 }} / {{ images.length }}
        </div>
        <button
          class="cn-product-images-slider__preview-next"
          @click="slideNext"
        >
          <UiIcon name="chevron-right" />
        </button>
      </div>
      <!-- thumbs -->
      <div class="cn-product-images-slider__thumbs">
        <CdnImg
          v-for="(image, index) in thumbImages"
          :key="index"
          provider="imgproxy"
          :src="image.src || '/img/placeholder.png'"
          :alt="image.alt"
          loading="lazy"
          :width="100"
          :height="100"
          format="webp"
          class="cn-product-images-slider__thumb"
          :class="getDynamicThumbClass(index, activeThumbIndex)"
          @click="onThumbImageClick(index)"
        />
      </div>
      <UiImagesGallery ref="gallery" :images="images" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import type { Swiper } from 'swiper';
import { useBreakpoints } from '@vueuse/core';
import { CdnImg } from '@webplatform/cdn-image';
import type { ImageWithAltType } from '~/types';
import UiImagesGallery from '@/components/ui/UiImagesGallery';
import UiIcon from '@/components/ui/UiIcon';

interface Props {
  images: ImageWithAltType[];
  thumbLimit?: number;
}
const props = defineProps<Props>();
const thumbImages = computed(() =>
  props.thumbLimit ? props.images.slice(0, props.thumbLimit) : props.images,
);

const isSliderImagesInit = ref(false);
const breakpoints = useBreakpoints({
  sm: 640,
  md: 992,
  lg: 1280,
  xl: 1656,
});
const lgAndLarger = breakpoints.greaterOrEqual('lg');

type SwiperContainerEl = HTMLElement & {
  swiper: Swiper;
  initialize: () => void;
};

const swiperEl = ref<SwiperContainerEl | null>(null);
const gallery = ref<InstanceType<typeof UiImagesGallery> | null>(null);

const activeThumbIndex = ref(0);

onMounted(() => {
  if (!swiperEl.value) return;
  Object.assign(swiperEl.value, {
    loop: true,
    slidesPerView: 1,
    on: {
      slideChange(swiper: Swiper) {
        activeThumbIndex.value = swiper.realIndex;
      },
    },
  });
  swiperEl.value.initialize();
  isSliderImagesInit.value = true;
  // Swiper measured the container while it was still `display: none` (v-show),
  // so recompute once it becomes visible to avoid a collapsed first render.
  nextTick(() => swiperEl.value?.swiper.update());
});

const getDynamicThumbClass = (thumbIndex: number, activeIndex: number) => {
  return {
    'cn-product-images-slider__thumb--active': thumbIndex === activeIndex,
  };
};

const onThumbImageClick = (thumbImageIndex: number) => {
  if (lgAndLarger.value) {
    swiperEl.value?.swiper.slideTo(thumbImageIndex);
  }
};

const slidePrev = () => swiperEl.value?.swiper.slidePrev();
const slideNext = () => swiperEl.value?.swiper.slideNext();
</script>

<style lang="scss">
.cn-product-images-slider {
  --ab-canyon-product-images-slider-thumb-color-hover: var(
    --ab-canyon-color-neutral-light
  );
  &__content {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }
  &__thumbs {
    width: 100%;
    display: none;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;

    @media (min-width: 1280px) {
      display: grid;
    }
  }

  &__thumb {
    outline: 1px solid var(--ab-canyon-color-neutral-light);
    outline-offset: -1px;
    cursor: pointer;

    &--active {
      outline: 2px solid var(--ab-canyon-color-neutral-dark);
      outline-offset: -2px;
    }
  }

  &__preview {
    width: 100%;
    display: grid;
    margin-left: auto;
    margin-right: auto;
    aspect-ratio: 1 / 1;

    @media (min-width: 1280px) {
      display: block;
    }

    img {
      object-fit: contain;
      width: 100%;
      height: 100%;
      cursor: pointer;
    }
  }
  &__preview-controls {
    justify-content: center;
    align-items: center;
    display: flex;
    @media (min-width: 1280px) {
      display: none;
    }

    .swiper-pagination {
      position: static;
      font-size: 18px;
      font-weight: 700;
      line-height: 26px;
      max-width: 100%;
      flex-basis: 180px;
      text-align: center;
      color: var(--ab-canyon-color-neutral-dark);
    }
  }
  &__preview-prev {
    margin-right: calc(var(--size-base) / 4);
    .cn-icon {
      width: var(--size-lg);
      height: var(--size-lg);
    }
  }
  &__preview-next {
    margin-left: calc(var(--size-base) / 4);
    .cn-icon {
      width: var(--size-lg);
      height: var(--size-lg);
    }
  }

  .swiper {
    &.gallery-top {
      width: 0;
      min-width: 100%;
      height: 100%;
    }
  }
}
</style>
